import React, { useState } from 'react';
import { promptService } from '../../services';
import { Prompt, Domain, PromptType, PromptUpdate } from '../../types';
import { Modal } from '../ui';

interface Props {
  prompt: Prompt | null;
  duplicateFrom?: Prompt | null;  // PROD1.7.7.4 — pré-remplissage depuis une autre config
  domains: Domain[];
  onClose: () => void;
  onSave: () => void;
}

export const PromptFormModal: React.FC<Props> = ({ prompt, duplicateFrom, domains, onClose, onSave }) => {
  const isEdit = !!prompt;
  const isDuplicate = !!duplicateFrom;
  const source = prompt || duplicateFrom;
  const [formData, setFormData] = useState({
    domain: source?.domain || (domains[0]?.name || ''),
    version: isDuplicate ? '' : (source?.version || ''),
    name: isDuplicate ? `${source?.name} (copie)` : (source?.name || ''),
    type: source?.type || 'extraction' as PromptType,
    content: source?.content || '',
    description: source?.description || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEdit) {
        // N'envoyer que les champs réellement modifiés : éviter de pousser
        // content/type inchangés qui déclencheraient la garde "prompt utilisé
        // dans des runs" côté backend (cf. update_prompt).
        const changes: PromptUpdate = {};
        if (formData.name !== prompt.name) changes.name = formData.name;
        if (formData.type !== prompt.type) changes.type = formData.type;
        if (formData.content !== prompt.content) changes.content = formData.content;
        if ((formData.description || '') !== (prompt.description || '')) {
          changes.description = formData.description || undefined;
        }
        await promptService.update(prompt.id, changes);
      } else {
        await promptService.create({
          domain: formData.domain,
          version: formData.version,
          name: formData.name,
          type: formData.type,
          content: formData.content,
          description: formData.description || undefined
        });
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier le Prompt' : (isDuplicate ? 'Dupliquer le Prompt' : 'Nouveau Prompt')}
      size="lg"
    >
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Domaine</label>
              <select
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                disabled={isEdit}
                className="w-full border rounded px-3 py-2"
                required
              >
                {domains.map((d, index) => (
                  <option key={`${d.name}-${index}`} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                disabled={isEdit}
                className="w-full border rounded px-3 py-2"
                placeholder="ex: 1.0, 1.1, 2.0"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nom</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as PromptType })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="extraction">Extraction</option>
                <option value="classification">Classification</option>
                <option value="validation">Validation</option>
                <option value="utility">Utility</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Contenu du Prompt</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full border rounded px-3 py-2 font-mono text-sm"
              rows={15}
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
    </Modal>
  );
};
